import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FoodItem} from "../../../../../../projects/vendors/src/lib/@domain/food-item";
import {FoodExtra} from "../../../../../../projects/other-orders/src/lib/@domain/food-extras";
import {RestaurantTable} from "../../../../../../projects/other-orders/src/lib/@domain/restaurent-table";
import {FoodItemQuantity} from "../../../../@domain/food-item-quantity";
import {ApiBaseService} from "../../../../../../projects/vendors/src/lib/services/api-base.service";
import {TableNamePipe} from "../../../../../../projects/vendors/src/lib/pipe/table-name.pipe";
import {AlertService} from "../../../../../../projects/vendors/src/lib/services/alert.service";
import {SoundService} from "../../../../../../projects/vendors/src/lib/services/sound.service";
import {VendorsService} from "../../../../../../projects/vendors/src/lib/vendors.service";
import {StorageService} from "../../../../../../projects/vendors/src/lib/services/storage.service";
import {FoodItemPipe} from "../../../../../../projects/vendors/src/lib/pipe/food-item.pipe";
import {Subscription} from "rxjs";
import {CartResponseDetail, OnGoingOrder} from "../../../../../../projects/other-orders/src/lib/@domain/on-going-order";
import {OrdersApiEnum} from "../../../../../../projects/other-orders/src/lib/@application/enums/orders-api-enum";
import {Kitchen} from "../../../../../../projects/other-orders/src/lib/@domain/kitchen";
import {OrderStatus} from "../../../../@application/enum/order-status.enum";
import {OrderType} from "../../../../@application/enum/order-type.enums";
import {PaymentStatus} from "../../../../@application/enum/payment-status.enum";

@Component({
    selector: 'app-kitchen-orders',
    templateUrl: './kitchen-orders.component.html',
    styleUrl: './kitchen-orders.component.scss',
    standalone: false
})
export class KitchenOrdersComponent implements OnInit, OnDestroy {
  orderTypes = OrderType
  orderStatus = OrderStatus
  paymentStatus = PaymentStatus
  foodItems = [] as FoodItem[];
  foodExtras = [] as FoodExtra[];
  tables = [] as RestaurantTable[];
  foodItemQuantity = [] as FoodItemQuantity[];
  apiBaseService = inject(ApiBaseService)
  tableNamePipe = inject(TableNamePipe)
  alertService = inject(AlertService)
  soundService = inject(SoundService)
  private foodNamePipe = inject(FoodItemPipe)
  vendorsService = inject(VendorsService);
  storageService = inject(StorageService)
  foodItemNamePipe = inject(FoodItemPipe)
  newOrderSubscription!: Subscription;
  orderStatusSubscription!: Subscription;
  orderCloseSubscription!: Subscription;
  userKitchenId!: string
  items: any[] = [];
  readyForPickupOnly = false;

  /**
   * Counter service is opt-in per tenant. When off, every behaviour below stays exactly
   * as it was for existing restaurants — this screen is in production.
   *
   * Read lazily, not latched in ngOnInit: HomeComponent writes 'restaurantDetail' from an
   * async GET, so on a hard refresh straight into this route it is not there yet and a
   * latched read would leave a counter tenant on the old two-state flow.
   */
  get counterService(): boolean {
    return this.storageService
      .getFromSessionStorage('restaurantDetail')?.counterServiceEnabled ?? false;
  }

  ngOnInit(): void {
    this.getUserKitchenDetails()
    this.newOrderSubscription = this.vendorsService.newOrderSubject.subscribe({
      next: value => {
        if (value) {
          this.soundService.playSound()
          this.getAllOngoingOrder()
        }
      }
    })
    this.orderStatusSubscription = this.vendorsService.orderStatusSubject.subscribe({
      next: value => {
        if (value) {
          this.soundService.playSound()
          this.getAllOngoingOrder()
        }
      }
    })
    this.orderCloseSubscription = this.vendorsService.orderCloseSubject.subscribe({
      next: value => {
        if (value) {
          this.soundService.playSound()
          this.getAllOngoingOrder()
        }
      }
    })
    this.getFoodItem();
    this.getFoodExtras();
    this.getTables()
    // this.getAllOngoingOrder()
    this.getFoodItemQuantity()

  }

  isMobileScreen(): boolean {
    return window.innerWidth <= 1200;
  }

  isNameContainsSpecialCharacter(name: string): boolean {
    const foodName = this.foodNamePipe.transform(name, this.foodItems);
    const bracketsRegex = /[()]/;
    return bracketsRegex.test(foodName);
  }

  get sortedItems(): any[] {
    const source = this.readyForPickupOnly
      ? this.items.filter(order => this.hasReadyItems(order))
      : this.items;
    return this.moveOrdersWithAllCartStatus3ToEnd([...source]);
  }

  /** Counter view: orders with at least one item made and waiting to be handed over. */
  hasReadyItems(order: any): boolean {
    return (order.cartResponseDetails ?? [])
      .some((item: any) => item.cartStatus === OrderStatus.Released);
  }

  toggleReadyForPickup() {
    this.readyForPickupOnly = !this.readyForPickupOnly;
  }

  getReadyForPickupCount(): number {
    return this.items.filter(order => this.hasReadyItems(order)).length;
  }

  /**
   * Orders needing action float to the top; finished ones sink.
   * Counter service order: all-Preparing, mixed/placed, all-Released (waiting on the
   * counter), all-Served.
   */
  moveOrdersWithAllCartStatus3ToEnd(orders: any[]) {
    if (!this.counterService) {
      // Existing restaurant behaviour — unchanged.
      // Separate orders with all cartStatus = 4 and the rest
      let ordersWithCartStatus3: any[] = [];
      let ordersWithCartStatus2: any[] = [];
      let otherOrders_: any[] = [];

      orders.forEach(order => {
        const allCartStatus3 = order.cartResponseDetails.every((item: any) => item.cartStatus === 3);
        const allCartStatus2 = order.cartResponseDetails.every((item: any) => item.cartStatus === 2);
        if (allCartStatus3) {
          ordersWithCartStatus3.push(order);
        } else if (allCartStatus2) {
          ordersWithCartStatus2.push(order);
        } else {
          otherOrders_.push(order);
        }
      });
      return [...ordersWithCartStatus2, ...otherOrders_, ...ordersWithCartStatus3];
    }

    // Counter service: Served is reachable, so it needs its own bucket at the end.
    const allPreparing: any[] = [];
    const allReleased: any[] = [];
    const allServed: any[] = [];
    const otherOrders: any[] = [];

    orders.forEach(order => {
      const details = order.cartResponseDetails ?? [];
      const every = (status: number) => details.length > 0
        && details.every((item: any) => item.cartStatus === status);

      if (every(OrderStatus.Served)) {
        allServed.push(order);
      } else if (every(OrderStatus.Released)) {
        allReleased.push(order);
      } else if (every(OrderStatus.Preparing)) {
        allPreparing.push(order);
      } else {
        otherOrders.push(order);
      }
    });
    return [...allPreparing, ...otherOrders, ...allReleased, ...allServed];
  }



  /**
   * Counter service only: Placed -> Preparing -> Released -> Served.
   * Terminal at Served; use sendBack() to undo.
   */
  private nextStatus(current: number): number | null {
    switch (current) {
      case OrderStatus.Placed:
        return OrderStatus.Preparing;
      case OrderStatus.Preparing:
        return OrderStatus.Released;
      case OrderStatus.Released:
        return OrderStatus.Served;
      default:
        return null;
    }
  }

  onStatusChange(foodItem: CartResponseDetail, orderId: string, tables: any) {
    if (this.counterService) {
      const next = this.nextStatus(foodItem.cartStatus);
      if (next === null) {
        return this.alertService.showInfo('Info', 'This item is already served')
      }
      foodItem.cartStatus = next;
    } else {
      if (foodItem.cartStatus === OrderStatus.Placed) {
        return this.alertService.showInfo('Info', 'Please wait for the order to be prepared')
      }
      foodItem.cartStatus = foodItem.cartStatus === OrderStatus.Preparing
        ? OrderStatus.Released
        : OrderStatus.Preparing;
    }
    const statusUpdate = {
      orderId: orderId,
      status: foodItem.cartStatus,
      itemName: this.foodItemNamePipe.transform(foodItem.foodId, this.foodItems),
      tableName: this.tableName(tables),
    }
    if (statusUpdate) {
      this.updateFoodStatus(statusUpdate, foodItem.id)
    }
  }

  /** Wrong item handed to the counter — send it back to be remade. Payment is untouched. */
  sendBack(foodItem: CartResponseDetail, orderId: string, tables: any) {
    foodItem.cartStatus = OrderStatus.Preparing;
    this.updateFoodStatus({
      orderId: orderId,
      status: OrderStatus.Preparing,
      itemName: this.foodItemNamePipe.transform(foodItem.foodId, this.foodItems),
      tableName: this.tableName(tables),
    }, foodItem.id)
  }

  canSendBack(foodItem: CartResponseDetail): boolean {
    return foodItem.cartStatus === OrderStatus.Released || foodItem.cartStatus === OrderStatus.Served;
  }

  // onCheckout(item: any) {
  //   this.dialog.open(OrderBillDialogComponent, {
  //     width: '40%',
  //     data: {
  //       onGoingOrder: item,
  //       foods: this.foodItems,
  //     }
  //   })
  // }

  sortOrderItems(cartResponseDetail: CartResponseDetail[]): CartResponseDetail[] {
    return cartResponseDetail.sort((a, b) => a.cartStatus < b.cartStatus ? -1 : 1)
  }


  getNonReleasedItemCount(): number {
    let count = 0;
    this.items.forEach(order => {
      count += order.cartResponseDetails.filter((item: any) =>
        item.cartStatus !== OrderStatus.Released &&
        item.cartStatus !== OrderStatus.Served
      ).length;
    });
    return count;
  }

  getNonCheckedOrderCount(): number {
    return this.items.filter(order =>
      this.checkOrderStatus(order.cartResponseDetails) === OrderStatus.Placed
    ).length;
  }


  private updateFoodStatus(data: any, foodItemId: string) {
    this.apiBaseService.PATCH_API([OrdersApiEnum.RestaurantModule, OrdersApiEnum.Cart, OrdersApiEnum.CartStatus, foodItemId], data)
      .subscribe({
        next: _value => {
        }
      })
  }

  getTables(): void {
    this.apiBaseService.GET<RestaurantTable[]>([OrdersApiEnum.RestaurantModule, OrdersApiEnum.RestaurantTable])
      .subscribe(res => {
        this.tables = res;

      })
  }

  getFoodExtras() {
    this.apiBaseService.GET<FoodExtra[]>([OrdersApiEnum.FoodModule, OrdersApiEnum.FoodExtra])
      .subscribe(res => {
        this.foodExtras = res;
      });
  }

  getFoodItem(): void {
    this.apiBaseService.GET<FoodItem[]>([OrdersApiEnum.FoodModule, OrdersApiEnum.Food])
      .subscribe(res => {
        this.foodItems = res;
      });
  }

  private getAllOngoingOrder(): void {
    this.apiBaseService.GET<OnGoingOrder[]>([OrdersApiEnum.RestaurantModule, OrdersApiEnum.Cart, OrdersApiEnum.GetAll])
      .subscribe({
          next: response => {
            this.items = this.filterOrdersByKitchen(response, this.userKitchenId);
          }
        }
      )
  }

  private filterOrdersByKitchen(orders: any[], kitchenId: string): any[] {
    return orders
      .map(order => {
        // Filter the cartResponseDetails that match the kitchenId
        const filteredCartDetails = order.cartResponseDetails.filter(
          (detail: any) => detail.kitchenId === kitchenId
        );

        // If there are no cart details matching the kitchenId, skip the order
        if (filteredCartDetails.length === 0) {
          return null;
        }

        // Return the order with filtered cartResponseDetails
        return {
          ...order,
          cartResponseDetails: filteredCartDetails
        };
      })
      .filter(order => order !== null); // Remove any null orders
  }

  getElapsedTime(createdTime: string): string {
    const now = new Date();
    const elapsedMs = now.getTime() - new Date(createdTime).getTime();

    const seconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day`;
    } else if (hours > 0) {
      return `${hours} hour`;
    } else if (minutes > 0) {
      return `${minutes} minute`;
    } else {
      return `${seconds} second`;
    }
  }


  tableName(tables: string[]): string {
    // Counter orders have no table; without this they render blank.
    if (tables.length == 0) {
      return 'POS'
    } else if (tables.length == 1) {
      return this.tables.find(x => x.id == tables[0])?.name!
    }
    return tables.map(x => this.tableNamePipe.transform(x, this.tables)).join(', ')
  }

  private getFoodItemQuantity(): void {
    this.apiBaseService.GET<FoodItemQuantity[]>([OrdersApiEnum.FoodModule, OrdersApiEnum.FoodItemQuantityTag])
      .subscribe(res => {
        this.foodItemQuantity = res
      });
  }

  ngOnDestroy(): void {
    this.newOrderSubscription.unsubscribe();
    this.orderStatusSubscription.unsubscribe();
    this.orderCloseSubscription.unsubscribe();
  }

  updateOrderStatus(order: any) {
    order.orderingStatus = order.orderingStatus === OrderStatus.Placed
      ? OrderStatus.Preparing
      : OrderStatus.Placed;
    this.apiBaseService.PATCH_API([OrdersApiEnum.RestaurantModule, OrdersApiEnum.Cart, OrdersApiEnum.OrderPreparationStatus], {
      orderId: order.orderId,
      status: order.orderingStatus,
      cartIds: order.cartResponseDetails.map((x: any) => x.id)
    })
      .subscribe({
        next: _value => {
          this.getAllOngoingOrder()
        }
      })

  }

  private getUserKitchenDetails() {
    const userDetails = this.storageService.getFromSessionStorage('userDetails')
    if (userDetails) {
      this.apiBaseService.GET<Kitchen>([OrdersApiEnum.RestaurantModule, OrdersApiEnum.Kitchen, OrdersApiEnum.GetByUserId, userDetails.id])
        .subscribe({
          next: value => {
            this.userKitchenId = value?.id
            this.getAllOngoingOrder()
          }
        })
    }
  }

  checkOrderStatus(cartResponseDetails: any[]) {
    if (!this.counterService) {
      // Existing restaurant behaviour — unchanged.
      return cartResponseDetails.some(x => x.cartStatus === OrderStatus.Preparing) ? OrderStatus.Preparing :
        cartResponseDetails.every(x => x.cartStatus === OrderStatus.Released) ? OrderStatus.Released :
          cartResponseDetails.some(x => x.cartStatus === OrderStatus.Served) ? OrderStatus.Served : OrderStatus.Placed
    }
    // Counter service: an order is only as advanced as its least-advanced line,
    // mirroring the backend rollup so the two cannot disagree.
    if (!cartResponseDetails?.length) return OrderStatus.Placed;
    return Math.min(...cartResponseDetails.map(x => x.cartStatus));
  }
}
